import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.ts';
import { usersApi } from '../features/users/api.ts';
import { orgSettingsApi } from '../features/orgSettings/api.ts';
import { dv } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';
import type { Body } from '../lib/body.ts';

const OFFICE_PROFILE_URL = 'https://office.wapplify.app/profile';

// Rendered for a body's PRESIDENT (council or WDC). Lets them hand over to that
// body's Vice President while on leave (the VP then acts as president, including
// signing). State is read fresh from /api/org for the caller's body so the
// switch reflects the live flag. The server derives the body from the role.
function PresidentLeaveSection({ body }: { body: Body }): JSX.Element {
  const qc = useQueryClient();
  const { data: org } = useQuery({ queryKey: ['org', body], queryFn: () => orgSettingsApi.get(body) });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const toggle = useMutation({
    mutationFn: (onLeave: boolean) => orgSettingsApi.setPresidentLeave(onLeave),
    onSuccess: () => {
      setError(false); setSaved(true);
      void qc.invalidateQueries({ queryKey: ['org', body] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: () => setError(true),
  });

  const onLeave = org?.presidentOnLeave ?? false;
  function onChange(): void {
    const next = !onLeave;
    const msg = next ? dv.president.leaveConfirmOn : dv.president.leaveConfirmOff;
    if (window.confirm(msg)) toggle.mutate(next);
  }

  return (
    <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-4">
      <h2 className="text-xl font-semibold leading-tight">{dv.president.leaveTitle}</h2>
      <p className="text-sm text-muted-foreground">{dv.president.leaveDescription}</p>
      <div className="flex items-center justify-between gap-3 rounded-2xl bg-[hsl(var(--surface-container-low))] px-4 py-3">
        <span className="text-sm font-medium">
          {dv.president.leaveLabel}
          <span className={`ms-2 ${onLeave ? 'text-[hsl(var(--warning))]' : 'text-muted-foreground'}`}>
            {onLeave ? dv.president.toggleOn : dv.president.toggleOff}
          </span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={onLeave}
          aria-label={dv.president.leaveLabel}
          disabled={toggle.isPending || !org}
          onClick={onChange}
          dir="ltr"
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            onLeave ? 'bg-[hsl(var(--warning))]' : 'bg-[hsl(var(--surface-container-highest))]'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              onLeave ? 'translate-x-[22px]' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
      {onLeave && <p className="text-sm text-[hsl(var(--warning))]">{dv.president.onLeaveOn}</p>}
      {saved && <p className="text-sm text-[hsl(var(--success))]" role="status">{dv.president.leaveSaved}</p>}
      {error && <p className="text-sm text-destructive" role="alert">{dv.common.error}</p>}
    </section>
  );
}

function NotifyNinmunSection(): JSX.Element {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me() });
  const [error, setError] = useState(false);
  const toggle = useMutation({
    mutationFn: (on: boolean) => usersApi.updateMe({ notifyNinmun: on }),
    onSuccess: () => { setError(false); void qc.invalidateQueries({ queryKey: ['me'] }); },
    onError: () => setError(true),
  });
  const on = me?.notifyNinmun ?? false;
  return (
    <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-4">
      <h2 className="text-xl font-semibold leading-tight">{dv.profile.notifyNinmunTitle}</h2>
      <p className="text-sm text-muted-foreground">{dv.profile.notifyNinmunHelp}</p>
      <div className="flex items-center justify-between gap-3 rounded-2xl bg-[hsl(var(--surface-container-low))] px-4 py-3">
        <span className="text-sm font-medium">{dv.profile.notifyNinmunLabel}</span>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={dv.profile.notifyNinmunLabel}
          disabled={toggle.isPending || !me}
          onClick={() => toggle.mutate(!on)}
          dir="ltr"
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${on ? 'bg-primary' : 'bg-[hsl(var(--surface-container-highest))]'}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
        </button>
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{dv.common.error}</p>}
    </section>
  );
}

export function Profile(): JSX.Element {
  const user = useAuthStore((s) => s.user);

  if (!user) {
    return (
      <div className="page">
        <p role="status" aria-live="polite" className="status-label">{dv.common.loading}</p>
      </div>
    );
  }

  const shownName = displayName(user);
  const initials = (shownName || user.email).slice(0, 2).toUpperCase();

  return (
    <div className="page space-y-6 lg:space-y-8">
      {/* Identity header — M3 card with tonal avatar circle */}
      <header className="flex items-center gap-4 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <div className="grid size-16 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] text-xl font-semibold">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="status-label">{dv.auth.profile}</p>
          <h1 className="text-2xl font-bold leading-tight mt-1 truncate tracking-tight">
            {shownName}
          </h1>
          <p className="mt-1 mono-meta text-muted-foreground truncate" dir="ltr">{user.email}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="pill pill-neutral">{user.designationDv ?? dv.roles[user.role]}</span>
            {user.designationEn && (
              <span className="mono-meta text-muted-foreground" dir="ltr">{user.designationEn}</span>
            )}
            {user.isOrgAdmin && (
              <span className="pill pill-success">{dv.orgAdminBadge}</span>
            )}
            {user.orgName && (
              <span className="mono-meta text-muted-foreground">{user.orgName}</span>
            )}
          </div>
        </div>
      </header>

      {/* President-only (council or WDC): hand over to that body's Vice
          President while on leave. */}
      {user.role === 'PRESIDENT' && <PresidentLeaveSection body="COUNCIL" />}
      {user.role === 'WDC_PRESIDENT' && <PresidentLeaveSection body="WDC" />}

      {/* All users: opt in to receiving signed ninmun emails. */}
      <NotifyNinmunSection />

      {/* Password is managed in Wapplify Office (single source of truth). */}
      <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-4">
        <h2 className="text-xl font-semibold leading-tight">{dv.auth.changePassword}</h2>
        <p className="text-sm text-muted-foreground">{dv.users.passwordInOffice}</p>
        <a
          href={OFFICE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex items-center gap-2"
        >
          {dv.users.openOffice}
          <Icon name="open_in_new" size={18} />
        </a>
      </section>
    </div>
  );
}
